import { useEffect } from 'react';
import { useParams } from "react-router";
import classNames from 'classnames';


import { PageModule, Survey, SurveyQuestion, QuizzesStatus } from "../../../../../types/working-model";
import { ParamsProps } from "../../../live-event";
import { getStylingOverrides } from "../../../utils";
import { TypographyItem } from "../../../../general-ui/typography-item/typography-item";
import Question from './question';
import { getTextValue } from "../../../../../utils/utils";
import '../../../../../scss/live-event/base/session/survey.scss';
import useTranslationRoute from '../../../hooks/use-translation-route';
import { useTranslate } from '../../../../../i18n/useTranslationModules';
import { useTypedSelector, useAppDispatch } from "../../../../../store/reducers/use-typed-selector";
import { checkQuizzesForCompletion } from '../../../../../store/actions/event/event-actions';
import { OptionalComponent } from '../../../../../utils/optional-component';

interface SurveyProps {
	module: PageModule;
	template: string;
	sessionUuid?: string;
	preview?: boolean;
}

export interface CompletedQuizQuestion {
	question: number;
	option: number;
}

export const getPollAnswerLocalStorageKey = (
	question: SurveyQuestion,
	surveyId: number,
	event: number
): string => {
	return `poll-answer_type:${question.question_type}_id:${question.survey_question}_survey:${surveyId}_event:${event}`;
};

// This is needed so older surveys that have already been take will be able to pull the key from local storage (typo and all)
export const getPollAnswerLocalStorageKeyBackwardsCompatible = (
	question: SurveyQuestion,
	surveyId: number
): string => {
	return `poll-answer_type:${question.question_type}_id:${question.survey_question}_survey:${surveyId}}`;
};

export const calculateFinishedSurvey = (module: PageModule, surveyQuestionsCompleted: CompletedQuizQuestion[]) => {
	const quizzes = module.modules;

	const _surveyQuestionsCompleted = [...surveyQuestionsCompleted.flat(Infinity)];
	const quizzesStatus: Record<number, QuizzesStatus> = {};
	const correctAnswers: Record<number, { correctOptions: number[], selectedOption: number, quizId: number, isCorrect: boolean }> = {};
	//	{ 114938: {correctOptions: [8354], selectedOption: 8354} };

	quizzes?.forEach((quiz: Survey) => {
		if (quiz.survey_type !== "quiz") {
			return;
		}
		// Creates the correctAnswers object that pulls in the selected option and correct option for each question
		quiz.questions.forEach((q: SurveyQuestion) => {
			q.options.forEach(o => {
				if (o.is_correct) {
					if (!(q.survey_question in correctAnswers)) {
						correctAnswers[q.survey_question] = { correctOptions: [], selectedOption: 0, quizId: quiz.survey, isCorrect: false };
					}
					correctAnswers[q.survey_question].correctOptions.push(o.survey_question_option);
				}
			});
		});

		// maps through the completed questions and declares if the selected answer is correct
		_surveyQuestionsCompleted.flat(Infinity).forEach(q => {
			if (q.question in correctAnswers) {
				correctAnswers[q.question].selectedOption = q.option;
				if (correctAnswers[q.question].correctOptions.includes(q.option)) {
					correctAnswers[q.question].isCorrect = true;
				}
			}
		});

		const quizQuestionIDs = quiz.questions.map((q: SurveyQuestion) => q.survey_question);
		quizzesStatus[quiz.survey] = { completed: false, correctAnswers: 0, totalQuestions: quizQuestionIDs, totalAnswered: 0, passed: false };

		// Fills out the quizzesStatus object for each quiz
		quizzesStatus[quiz.survey].totalQuestions.forEach(q => {

			if (correctAnswers[q].selectedOption > 0) {
				quizzesStatus[quiz.survey].totalAnswered++;
			}

			if (correctAnswers[q].isCorrect) {
				quizzesStatus[quiz.survey].correctAnswers++;
			}

			if (quizzesStatus[quiz.survey].totalAnswered === quizzesStatus[quiz.survey].totalQuestions.length) {
				quizzesStatus[quiz.survey].completed = true;
			}
		});

		if (quiz?.passing_threshold && quizzesStatus[quiz.survey].correctAnswers >= quiz?.passing_threshold) {
			quizzesStatus[quiz.survey].passed = true;
		}
	});

	return quizzesStatus;
};


const SessionSurvey: React.FC<SurveyProps> = ({ module, template, sessionUuid, preview }) => {
	const dispatch = useAppDispatch();
	const { language } = useParams() as ParamsProps;
	const { content, styling_overrides } = module;
	const stylingOverrides = getStylingOverrides(styling_overrides);
	const namespace = useTranslationRoute();
	const { t } = useTranslate(namespace);

	const sessions = useTypedSelector((state) => state.LiveEventReducer.eventBundle?.sessions);
	const event = useTypedSelector((state) => state.LiveEventReducer.eventBundle?.event);

	const {
		backgroundColor,
		backgroundImg,
		customHeadingOverrides,
		moduleTitleOverrides,
		titleOverrides,
	} = stylingOverrides;

	const { custom_heading, title } = getTextValue(content, language);

	// This checks quizzes for completion; does not set completed answer values
	useEffect(() => {
		if (!event || !sessions) return;
		dispatch(checkQuizzesForCompletion(module, event, sessions));
	}, [dispatch, event, module, sessions]);

	const surveysCustomHeaderTitle = () => t(`${namespace}:survey.${module.id}.title`, t(`${namespace}:survey.title`, title));

	if (preview) {
		return (
			<section
				className={classNames(
					'surveys session-module',
					template,
					backgroundColor
				)}>
				<div className={classNames({ 'module-container': !sessionUuid })}> {/* TODO: convert this div to a conditional wrapper component when that component goes live */}
					<TypographyItem className={classNames('surveys-custom-heading', customHeadingOverrides, { 'surveys-custom-heading-event': !sessionUuid })} tagName="p">
						{t(`${namespace}:survey.${module.id}.heading`, t(`${namespace}:survey.heading`, custom_heading))}
					</TypographyItem>
					<OptionalComponent display={!surveysCustomHeaderTitle()}>
						<TypographyItem className={classNames('evt-heading-2 surveys-custom-header-title', customHeadingOverrides)} tagName="h2">
							{surveysCustomHeaderTitle()}
						</TypographyItem>
					</OptionalComponent>
					{!module?.modules?.length ? null : (
						module.modules.map((survey: Survey) => {
							return (
								<div key={`survey-container-${survey.survey}`} className={classNames('survey-container')}>
									<TypographyItem className={classNames('survey-title evt-heading-4', template, titleOverrides)} tagName="p">
										{t(`${namespace}:survey.${survey.survey}.name`, survey.name[language] as string ?? survey.name.base)}
									</TypographyItem>
									<Question sessionUuid={sessionUuid} survey={survey} module={module} template={template} preview={preview} />
								</div>
							);
						}))}
				</div>
			</section>
		);
	} else {

		const renderSurveyQuestions = () => {
			if (!module?.modules?.length) return null;
			return module.modules.map((survey: Survey) => {
				return (
					<div key={`survey-container-${survey.survey}`} className={classNames('survey-container')}>
						<TypographyItem className={classNames('survey-title evt-heading-4', template, moduleTitleOverrides)} tagName="p">
							{survey?.name?.[language] ?? survey?.name?.base}
						</TypographyItem>
						<Question sessionUuid={sessionUuid} survey={survey} module={module} template={template} />
					</div>
				);
			});
		};

		if (!module?.modules?.length) return null;

		return (
			<section
				className={classNames(
					'surveys session-module surveys-content-home',
					template,
					backgroundColor
				)}
				style={backgroundImg}
			>
				<div className={classNames({ 'module-container': !sessionUuid })}>
					<TypographyItem className={classNames('surveys-custom-heading', customHeadingOverrides, { 'surveys-custom-heading-event': !sessionUuid })} tagName="p">
						{t(`${namespace}:survey.${module.id}.heading`, t(`${namespace}:survey.heading`, custom_heading))}
					</TypographyItem>
					<OptionalComponent display={!surveysCustomHeaderTitle()}>
						<TypographyItem className={classNames('evt-heading-2 surveys-custom-header-title', titleOverrides)} tagName="h2">
							{surveysCustomHeaderTitle()}
						</TypographyItem>
					</OptionalComponent>

					{renderSurveyQuestions()}
				</div>
			</section>
		);
	}
};

export default SessionSurvey;